import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../api";
import { useAuth } from "../auth";
import { money } from "../calc";
import { generateInvoicePDF } from "../pdf";

const pad2 = (x) => String(x).padStart(2, "0");
const fmtCountdown = (s) => `${pad2(Math.floor(s / 60))}:${pad2(s % 60)}`;

export default function AssinaturaTab({ settings }) {
  const { loadMe } = useAuth();
  const [data, setData] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState([]);
  const [pixForm, setPixForm] = useState({ cpf: "", name: "", email: "" });
  const [pix, setPix] = useState(null);
  const [showPix, setShowPix] = useState(false);
  const [pixLoading, setPixLoading] = useState(false);
  const [pixPaid, setPixPaid] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(null);
  const pixPoll = useRef(null);
  const pixTick = useRef(null);

  const load = () => {
    api.get("/subscription").then((r) => setData(r.data)).catch(() => {});
    api.get("/billing/invoices").then((r) => setInvoices(r.data)).catch(() => {});
    api.get("/plans").then((r) => setPlans(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const stopTimers = () => { clearInterval(pixPoll.current); clearInterval(pixTick.current); };
  useEffect(() => () => stopTimers(), []);

  const selectPlan = async (pid) => {
    setLoading(true);
    try { await api.post("/billing/select-plan", { planId: pid }); toast.success("Plano selecionado."); load(); loadMe(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  const payMonthly = async () => {
    setLoading(true);
    try {
      const { data: res } = await api.post("/billing/charge");
      if (res.checkoutUrl) window.location.assign(res.checkoutUrl);
      else toast.error("Não foi possível gerar o pagamento.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  const openPix = () => {
    stopTimers(); setPix(null); setPixPaid(false); setSecondsLeft(null);
    setPixForm((f) => ({ ...f, email: f.email || data?.company?.email || "" }));
    setShowPix(true);
  };
  const closePix = () => { stopTimers(); setShowPix(false); setPix(null); setPixPaid(false); setSecondsLeft(null); };

  const startCountdown = (expiresAt) => {
    clearInterval(pixTick.current);
    if (!expiresAt) return;
    const target = new Date(expiresAt).getTime();
    const tick = () => {
      const s = Math.max(0, Math.round((target - Date.now()) / 1000));
      setSecondsLeft(s);
      if (s <= 0) stopTimers();
    };
    tick();
    pixTick.current = setInterval(tick, 1000);
  };

  const genPix = async () => {
    if (!pixForm.name.trim() || pixForm.cpf.replace(/\D/g, "").length !== 11) return toast.error("Informe nome e CPF (11 dígitos).");
    setPixLoading(true);
    try {
      const { data: res } = await api.post("/billing/pix", pixForm);
      setPix(res);
      setPixPaid(false);
      startCountdown(res.expires_at);
      clearInterval(pixPoll.current);
      pixPoll.current = setInterval(async () => {
        try {
          const { data: st } = await api.get(`/billing/pix/${res.payment_id}/status`);
          if (st.status === "approved") { stopTimers(); setPixPaid(true); toast.success("Pagamento PIX confirmado!"); load(); loadMe(); }
        } catch { /* ignore */ }
      }, 6000);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setPixLoading(false); }
  };
  const cancel = async () => {
    if (!window.confirm("Cancelar a assinatura?")) return;
    try { await api.post("/billing/cancel"); toast.success("Assinatura cancelada."); load(); loadMe(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const sync = async () => {
    setLoading(true);
    try {
      const { data: res } = await api.post("/billing/sync");
      toast.success(res.invoicesCreated ? `Sincronizado. ${res.invoicesCreated} recibo(s) importado(s).` : "Status sincronizado com o Mercado Pago.");
      load();
      loadMe();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  const company = data?.company, plan = data?.plan, sub = data?.subscription;
  const isActive = company?.status === "active" || company?.subscriptionStatus === "active";
  const nextBilling = company?.nextBillingDate || sub?.nextBillingDate;
  const charge = data?.charge;
  const usage = data?.usage;
  const usageFmt = (used, max) => (max ? `${used ?? 0} de ${max}` : `${used ?? 0} (ilimitado)`);
  const nearRemoval = plan?.maxRemovals && usage?.removals != null && usage.removals >= Math.ceil(plan.maxRemovals * 0.8);
  const statusMap = { trial: "Em teste", active: "Ativa", past_due: "Inadimplente", suspended: "Suspensa", cancelled: "Cancelada" };
  const pixExpired = secondsLeft === 0;
  return (
    <section className="panel active">
      <div className="module-head"><div><h1>Assinatura</h1><p>Pague por cartão ou PIX no checkout seguro do Mercado Pago. Assinatura mensal recorrente.</p></div></div>
      {nearRemoval && (
        <div className="trial-banner danger" data-testid="assinatura-removal-banner">
          {usage.removals >= plan.maxRemovals
            ? `Você atingiu o limite de ${plan.maxRemovals} remoções deste mês no plano ${plan.name}.`
            : `Você já usou ${usage.removals} de ${plan.maxRemovals} remoções deste mês no plano ${plan.name}.`} Assine um plano maior abaixo para liberar mais remoções.
        </div>
      )}
      <div className="plan-select-grid" data-testid="plan-select">
        {plans.slice().sort((a, b) => (a.price || 0) - (b.price || 0)).map((p) => {
          const current = plan?.id === p.id;
          const recommended = /profission/i.test(p.name || "");
          const isDiff = (ft) => /(ve[íi]culo|remo[çc]|usu[áa]rio|ilimitad)/i.test(ft);
          return (
            <div className={`plan-select-card ${current ? "current" : ""} ${recommended && !current ? "recommended" : ""}`} key={p.id} data-testid={`plan-opt-${p.id}`}>
              {current ? <span className="ps-badge">Seu plano</span> : recommended ? <span className="ps-badge rec">Recomendado</span> : null}
              <div className="ps-name">{p.name}</div>
              <div className="ps-price">{money(p.price)}<small>/mês</small></div>
              <ul>{(p.features || []).map((ft, i) => <li key={ft || i} className={isDiff(ft) ? "ps-diff" : ""}>{ft}</li>)}</ul>
              {current ? (
                <button className="secondary" disabled data-testid={`plan-select-${p.id}`}>Plano atual</button>
              ) : (
                <button className="primary" disabled={loading} onClick={() => selectPlan(p.id)} data-testid={`plan-select-${p.id}`}>Escolher este plano</button>
              )}
            </div>
          );
        })}
      </div>
      <div className="module-grid two-col">
        <article className="card">
          <h2>Plano {plan?.name || "RotaSaúde"}</h2>
          <div className="final-price"><span>Mensalidade</span><strong data-testid="plan-price">{money(plan?.price || 0)}</strong></div>
          <ul style={{ margin: "16px 0", paddingLeft: 18, color: "#435a71", fontSize: 13, lineHeight: 1.8 }}>
            {(plan?.features || []).map((ft, i) => <li key={ft || i}>{ft}</li>)}
          </ul>
          <p className="hint" style={{ marginBottom: 12 }}>Pague a mensalidade sem precisar de conta Mercado Pago. O recebimento cai na conta Mercado Pago do RotaSaúde.</p>
          <button className="primary full" onClick={payMonthly} disabled={loading} data-testid="btn-pay">{loading ? "Abrindo pagamento…" : "Pagar com cartão ou boleto"}</button>
          <button className="secondary full" style={{ marginTop: 8 }} onClick={openPix} data-testid="btn-pix">Pagar com PIX (QR Code)</button>
          {charge?.status === "paid" && <p className="hint" style={{ marginTop: 10, color: "#20b35a", fontWeight: 700 }} data-testid="charge-paid">✓ Mensalidade de {charge.period} já paga.</p>}
          {showPix && (
            <div className="pix-box" data-testid="pix-box" style={{ marginTop: 14, padding: 14, border: "1px solid #cfdde8", borderRadius: 12 }}>
              {!pix ? (
                <>
                  <p style={{ fontWeight: 700, marginBottom: 8 }}>Gerar PIX ({money(plan?.price || 0)})</p>
                  <input placeholder="Nome do pagador" value={pixForm.name} onChange={(e) => setPixForm({ ...pixForm, name: e.target.value })} data-testid="pix-name" style={{ width: "100%", marginBottom: 6 }} />
                  <input placeholder="CPF (somente números)" value={pixForm.cpf} onChange={(e) => setPixForm({ ...pixForm, cpf: e.target.value })} data-testid="pix-cpf" style={{ width: "100%", marginBottom: 6 }} />
                  <input placeholder="E-mail" value={pixForm.email} onChange={(e) => setPixForm({ ...pixForm, email: e.target.value })} data-testid="pix-email" style={{ width: "100%", marginBottom: 8 }} />
                  <button className="primary full" onClick={genPix} disabled={pixLoading} data-testid="pix-generate">{pixLoading ? "Gerando…" : "Gerar QR Code PIX"}</button>
                  <button className="secondary full" style={{ marginTop: 6 }} onClick={closePix}>Cancelar</button>
                </>
              ) : pixPaid ? (
                <div style={{ textAlign: "center", padding: "10px 0" }} data-testid="pix-paid">
                  <div style={{ fontSize: 46, lineHeight: 1, color: "#20b35a" }}>✓</div>
                  <p style={{ fontSize: 17, fontWeight: 800, color: "#0a6f3c", margin: "8px 0 2px" }}>Pagamento confirmado!</p>
                  <p className="hint" style={{ marginBottom: 12 }}>Sua mensalidade de {pix.period} foi recebida. Obrigado!</p>
                  <button className="primary full" onClick={closePix} data-testid="pix-close">Fechar</button>
                </div>
              ) : (
                <div style={{ textAlign: "center" }}>
                  {pix.qr_code_base64 && <img src={`data:image/png;base64,${pix.qr_code_base64}`} alt="QR Code PIX" data-testid="pix-qr" style={{ width: 220, height: 220, opacity: pixExpired ? 0.4 : 1 }} />}
                  <p style={{ fontSize: 13, fontWeight: 700, margin: "6px 0" }}>Escaneie o QR Code ou use o PIX copia e cola:</p>
                  <textarea readOnly value={pix.qr_code || ""} data-testid="pix-copiacola" style={{ width: "100%", height: 64, fontSize: 11 }} onFocus={(e) => e.target.select()} />
                  <button className="secondary full" style={{ marginTop: 6 }} onClick={() => { navigator.clipboard.writeText(pix.qr_code || ""); toast.success("Código PIX copiado!"); }} data-testid="pix-copy" disabled={pixExpired}>Copiar código PIX</button>
                  <p className="hint" style={{ marginTop: 10, fontSize: 12, color: "#6d8094", textAlign: "left", lineHeight: 1.5, background: "#f2f7fb", border: "1px solid #cfdde8", borderRadius: 8, padding: 10 }} data-testid="pix-antifraud">
                    <strong>Aviso de segurança do banco:</strong> se o app do seu banco exibir um alerta de "suspeita de golpe" na primeira transferência, fique tranquilo — é uma proteção padrão para pagamentos a novos destinatários. O pagamento é legítimo e vai para a conta do RotaSaúde. Basta confirmar para continuar.
                  </p>
                  {secondsLeft != null && !pixExpired && (
                    <p className="hint" style={{ marginTop: 8 }} data-testid="pix-countdown">QR Code válido por <strong>{fmtCountdown(secondsLeft)}</strong></p>
                  )}
                  {pixExpired ? (
                    <div style={{ marginTop: 8 }} data-testid="pix-expired">
                      <p className="hint" style={{ color: "#d0433b", fontWeight: 700 }}>PIX expirado. Gere um novo QR Code.</p>
                      <button className="primary full" style={{ marginTop: 6 }} onClick={() => { stopTimers(); setPix(null); setSecondsLeft(null); }} data-testid="pix-regen">Gerar novo PIX</button>
                    </div>
                  ) : (
                    <p className="hint" style={{ marginTop: 8 }} data-testid="pix-waiting">Aguardando confirmação do pagamento…</p>
                  )}
                </div>
              )}
            </div>
          )}
        </article>
        <article className="card">
          <h2>Situação da conta</h2>
          <div className="result-line"><span>Status da empresa</span><strong data-testid="sub-status">{statusMap[company?.status] || company?.status || "—"}</strong></div>
          <div className="result-line"><span>Fim do período de teste</span><strong data-testid="sub-trial">{isActive ? "Encerrado (assinatura ativa)" : (company?.trialEndsAt ? new Date(company.trialEndsAt).toLocaleDateString("pt-BR") : "—")}</strong></div>
          <div className="result-line"><span>Assinatura</span><strong data-testid="sub-plan">{isActive ? `Ativa — ${plan?.name || "RotaSaúde"}` : (sub ? (statusMap[sub.status] || sub.status) : "Nenhuma")}</strong></div>
          <div className="result-line"><span>Próxima cobrança</span><strong data-testid="sub-next">{nextBilling ? new Date(nextBilling).toLocaleDateString("pt-BR") : "—"}</strong></div>
          <div className="result-line"><span>Veículos cadastrados</span><strong data-testid="usage-vehicles">{usageFmt(usage?.vehicles, plan?.maxVehicles)}</strong></div>
          <div className="result-line"><span>Remoções neste mês</span><strong data-testid="usage-removals">{usageFmt(usage?.removals, plan?.maxRemovals)}</strong></div>
          <div className="result-line"><span>Usuários</span><strong data-testid="usage-users">{usageFmt(usage?.users, plan?.maxUsers)}</strong></div>
          {sub && <button className="secondary" style={{ marginTop: 14, marginRight: 8 }} onClick={sync} disabled={loading} data-testid="btn-sync-sub">{loading ? "Sincronizando…" : "Sincronizar status"}</button>}
          {sub && sub.status !== "cancelled" && <button className="secondary" style={{ marginTop: 14 }} onClick={cancel} data-testid="btn-cancel-sub">Cancelar assinatura</button>}
        </article>
      </div>
      <article className="card" style={{ marginTop: 16 }}>
        <h2>Recibos de mensalidade</h2>
        {invoices.length === 0 ? (
          <p className="hint">Nenhuma mensalidade paga ainda. Os recibos aparecem aqui automaticamente após cada pagamento confirmado.</p>
        ) : (
          <div className="module-list compact">
            {invoices.map((inv) => (
              <div className="list-card" key={inv.id} data-testid={`invoice-${inv.id}`}>
                <div><strong>{money(inv.amount)}</strong><span>Pago em {inv.paidAt ? new Date(inv.paidAt).toLocaleDateString("pt-BR") : "—"}</span><small>Recibo nº {(inv.providerPaymentId || inv.id || "").toString().slice(-8).toUpperCase()}</small></div>
                <div className="list-value"><button onClick={() => generateInvoicePDF(inv, settings)} data-testid={`invoice-pdf-${inv.id}`}>Baixar recibo (PDF)</button></div>
              </div>
            ))}
          </div>
        )}
      </article>
    </section>
  );
}
